import { useMemo } from 'react';

import type { IUnit } from '@/service/vpc/types/IUnit';

import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { chassis, telemetry } from '@/utils';

// Layout key constants
export const LAYOUT_KEYS = {
  UNITS_ONE: 'UNITS_ONE',
  UNITS_MANY: 'UNITS_MANY',
  LOADING: 'LOADING',
  NO_UNITS: 'NO_UNITS',
} as const;

export type LayoutKey = (typeof LAYOUT_KEYS)[keyof typeof LAYOUT_KEYS];

// Segment mappings
// const INDUSTRIAL_SEGMENTS = ['IV', 'IG'];
// const MARINE_COMMERCIAL_SEGMENTS = ['MC'];
// const MARINE_LEISURE_SEGMENTS = ['MLD', 'MLG', 'MO'];

type TUseLayoutOptions = {
  units?: IUnit[];
  unitsLoading: boolean;
};

type TLayoutResult = {
  layoutKey: LayoutKey;
  canShowDashboard: boolean | undefined;
};

export const useLayout = ({ units, unitsLoading }: TUseLayoutOptions): TLayoutResult => {
  const { userHasCapability } = useFleetAccount();

  return useMemo(() => {
    // Loading state
    if (unitsLoading) {
      return {
        layoutKey: LAYOUT_KEYS.LOADING,
        canShowDashboard: undefined,
      };
    }

    // No units state
    if (!units || units.length === 0) {
      return {
        layoutKey: LAYOUT_KEYS.NO_UNITS,
        canShowDashboard: false,
      };
    }

    // Dashboard insights require at least one TGW unit with an Active service, so marine TGW
    // units get the dashboard while EC/EC_Wifi-only units stay excluded (AB#1498723, released
    // to prod in AB#1370313). IPS40 marine units (VPDRV driveline) always qualify (AB#1611582).
    const hasDashboardUnit = units.some(
      (unit) =>
        chassis.isIps40(unit) ||
        (telemetry.hasTelemetryDeviceType(unit.telemetryDeviceInfo, 'TGW') &&
          unit.serviceStatuses?.some((service) => service.status === 'Active'))
    );

    const hasTelematicsPermission = units.every((unit) =>
      userHasCapability('unit.telematics.view', unit.endCustomerUnitId)
    );

    const canShowDashboard = hasTelematicsPermission && hasDashboardUnit;
    const layoutPrefix = units.length > 1 ? LAYOUT_KEYS.UNITS_MANY : LAYOUT_KEYS.UNITS_ONE;

    return {
      layoutKey: layoutPrefix as LayoutKey,
      canShowDashboard,
    };
  }, [units, unitsLoading, userHasCapability]);
};

export default useLayout;
